import { FormattingRule, FormattingRuleType, getFormattingRules, PositionInstructions, StartEndRowCol, startEndRowColToCellPosition } from "@coolset/cs-ui";
import { isEqual } from "lodash";
import { conditionClient } from "modules/Conditions/client/conditionClient";
import { Conditions } from "modules/Conditions/conditions";
import { relatedSupportingInfoClient } from "modules/SupportingInfo/client/relatedSupportingInfoClient";
import { useCallback, useMemo, useRef, useState } from "react";
import { validate } from "shared/helpers/validate";
import { mainSurveyClient } from "../client/mainSurveyClient";
import { BlockSubTypes, FieldIdPrefixes } from "../helpers/blocks";
import { Blockable } from "../helpers/blockTree";
import { SurveyBlocks } from "../surveyBlocks";

type StageResult = { success: true } | { success: false; errorText: string };

export const useCopyAndPasteBlocks = () => {
  const [isPasteDialogOpen, setIsPasteDialogOpen] = useState<boolean>(false);
  const [copiedBlocks, setCopiedBlocks] = useState<Blockable[] | null>([]);
  const [blocksToPaste, setBlocksToPaste] = useState<BlockToPaste[]>([]);
  const [pasteParentId, setPasteParentId] = useState<string | number | null>(null);
  const [pasteUpperParentIds, setUpperPasteParentIds] = useState<{
    surveyId?: number | string;
    id: number | string;
  } | null>(null);
  const [pasteToIndex, setPasteToIndex] = useState<number | null>(null);
  const [loaderText, setLoaderText] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [blockError, setBlockError] = useState<{ blockId?: string | number; text: string } | null>(null);
  const [isPasteConditions, setIsPasteConditions] = useState<boolean>(true);
  const [isPasteRelatedSupportingInfo, setIsPasteRelatedSupportingInfo] = useState<boolean>(true);
  const [copiedFormattingInstructions, setCopiedFormattingInstructions] = useState<FormattingRule[]>([]);
  const [copiedPositionInstructions, setCopiedPositionInstructions] = useState<PositionInstructions | null>(null);
  const [formattingInstructionsToPaste, setFormattingInstructionsToPaste] = useState<FormattingRule[]>([]);
  const [positionInstructionsToPaste, setPositionInstructionsToPaste] = useState<PositionInstructions | null>(null);
  const [tableScale, setTableScale] = useState<{ maxRows: number; maxColumns: number } | null>(null);
  const [pasteAnchorRow, setPasteAnchorRow] = useState<number>(1);
  const [pasteAnchorCol, setPasteAnchorCol] = useState<number>(1);
  // useRef so callers can pass a plain async function without useState's "call if function" quirk
  const onSuccessRef = useRef<
    ((data: {
      newPositionInstructions?: PositionInstructions;
      newTableScale?: { maxRows: number; maxColumns: number };
      createdBlocks?: BlockToPaste[];
    }) => void) | null
  >(null);

  // True when the clipboard contains table blocks (with position instructions)
  const isTablePaste = copiedPositionInstructions !== null;

  const canCreate = useMemo<boolean>(
    () =>
      blocksToPaste.every(
        (block) =>
          (!block.oldFieldId || block.oldFieldId !== block.newFieldId || block.isPasteAsExisting) &&
          (!block.newFieldId || validate.isValidFieldId(block.newFieldId)) &&
          (block.childBlocks ?? []).every(
            (child) => !child.newFieldId || validate.isValidFieldId(child.newFieldId)
          )
      ),
    [blocksToPaste]
  );

  const getBlocksToPaste = useCallback(async () => {
    if (!copiedBlocks) return;

    setBlockError(null);
    setIsLoading(true);
    setLoaderText(`Copying block (1/${copiedBlocks.length})`);
    const blocksToPaste = [] as BlockToPaste[];
    for (const block of copiedBlocks) {
      try {
        const blockData = (await mainSurveyClient.getSurveyBlock(block.parentSurveyId, block.id)).data;
        const fieldIdWithoutPrefix =
          (blockData.contentObject.fieldId ?? "").split(FieldIdPrefixes[blockData.contentType] + "_")[1] ?? "";
        const blockToPaste: BlockToPaste = {
          parentSurveyId: block.parentSurveyId,
          id: block.id,
          oldFieldId: fieldIdWithoutPrefix,
          newFieldId: fieldIdWithoutPrefix,
          oldObjectId: block.objectId,
          isCreated: false,
          blockData,
        };

        if (
          blockData.contentObject.type === BlockSubTypes.Table ||
          blockData.contentObject.type === BlockSubTypes.Chart
        ) {
          const { data: { results: childBlocks } } = await mainSurveyClient.getSurveyBlocks(blockData.objectId);
          blockToPaste.childBlocks = childBlocks.map((child) => {
            const childFieldId =
              (child.contentObject.fieldId ?? "").split(FieldIdPrefixes[child.contentType] + "_")[1] ?? "";
            return {
              oldId: child.id,
              oldObjectId: child.objectId,
              oldFieldId: childFieldId,
              newFieldId: childFieldId,
              isCreated: false,
              blockData: child,
            };
          });
        }

        blocksToPaste.push(blockToPaste);
        setLoaderText(`Copying block (${blocksToPaste.length + 1}/${copiedBlocks.length})`);
      } catch (e) {
        setBlockError({ blockId: block.id, text: "Failed to copy block" });
        setLoaderText("");
        setIsLoading(false);
        return;
      }
    }

    setBlocksToPaste(blocksToPaste);
    setLoaderText("");
    setIsLoading(false);
  }, [copiedBlocks]);

  const startPaste = useCallback(
    (
      toIndex: number,
      parentId?: string | number,
      upperParentIds?: { surveyId?: number | string; id: number | string },
      tableScale?: { maxRows: number; maxColumns: number },
      onSuccess?: (data: {
        newPositionInstructions?: PositionInstructions;
        newTableScale?: { maxRows: number; maxColumns: number };
        createdBlocks?: BlockToPaste[];
      }) => void
    ) => {
      if (!parentId) return;

      onSuccessRef.current = onSuccess ?? null;
      setPasteParentId(parentId);
      setUpperPasteParentIds(upperParentIds ?? null);
      setTableScale(tableScale ?? null);
      setPasteToIndex(toIndex);
      setIsPasteDialogOpen(true);
      getBlocksToPaste();
    },
    [getBlocksToPaste]
  );

  const cancelPaste = useCallback(() => {
    setIsPasteDialogOpen(false);
    setBlocksToPaste([]);
    setCopiedBlocks(null);
    setPasteParentId(null);
    setUpperPasteParentIds(null);
    setTableScale(null);
    setPasteToIndex(null);
    setPasteAnchorRow(1);
    setPasteAnchorCol(1);
    setLoaderText("");
    setIsLoading(false);
    setBlockError(null);
    onSuccessRef.current = null;
  }, []);

  const findAndReplaceForAllFieldIds = useCallback((stringToFind: string, stringToReplace: string) => {
    setBlocksToPaste((blocks) =>
      blocks.map((block) => ({
        ...block,
        newFieldId: block.newFieldId.replace(stringToFind, stringToReplace),
        childBlocks: block.childBlocks?.map((child) => ({
          ...child,
          newFieldId: child.newFieldId.replace(stringToFind, stringToReplace),
        })),
      }))
    );
  }, []);

  const handleNewFieldIdChange = useCallback((blockId: string | number, newFieldId: string) => {
    setBlocksToPaste((blocks) => blocks.map((block) => (block.id === blockId ? { ...block, newFieldId } : block)));
  }, []);

  const handleIsPasteAsExistingChange = useCallback((blockId: string | number, isPasteAsExisting: boolean) => {
    setBlocksToPaste((blocks) =>
      blocks.map((block) => (block.id === blockId ? { ...block, isPasteAsExisting } : block))
    );
  }, []);

  const handleIsPasteAsExistingChildChange = useCallback(
    (tableBlockId: string | number, childOldId: number, isPasteAsExisting: boolean) => {
      setBlocksToPaste((blocks) =>
        blocks.map((block) =>
          block.id !== tableBlockId
            ? block
            : {
                ...block,
                childBlocks: block.childBlocks?.map((child) =>
                  child.oldId === childOldId ? { ...child, isPasteAsExisting } : child
                ),
              }
        )
      );
    },
    []
  );

  const handleNewChildFieldIdChange = useCallback(
    (tableBlockId: string | number, childOldId: number, newFieldId: string) => {
      setBlocksToPaste((blocks) =>
        blocks.map((block) =>
          block.id !== tableBlockId
            ? block
            : {
                ...block,
                childBlocks: block.childBlocks?.map((child) =>
                  child.oldId === childOldId ? { ...child, newFieldId } : child
                ),
              }
        )
      );
    },
    []
  );

  const removeBlockToPaste = useCallback((blockId: string | number) => {
    setBlocksToPaste((blocks) => blocks.filter((block) => block.id !== blockId));
  }, []);

  // rowOffset and colOffset are computed in createBlocks from the anchor and the copied selection's
  // top-left corner, then passed into each handler so offset is only calculated once.
  const handleChangeTableScale = useCallback(
    async (
      rowOffset: number,
      colOffset: number
    ): Promise<StageResult & { newTableScale?: { maxRows: number; maxColumns: number } }> => {
      if (!pasteUpperParentIds?.surveyId || !pasteUpperParentIds?.id || !tableScale) {
        const errorText = "could not find the parent or max row column of the table";
        setBlockError({ text: errorText });
        return { success: false, errorText };
      }

      const positions = Object.values(copiedPositionInstructions ?? {}).filter((p): p is StartEndRowCol => p !== undefined);
      const requiredRows = positions.length
        ? Math.max(...positions.map((p) => p.rowEnd + rowOffset)) + 1
        : tableScale.maxRows;
      const requiredCols = positions.length
        ? Math.max(...positions.map((p) => p.colEnd + colOffset)) + 1
        : tableScale.maxColumns;
      const newTableScale = {
        maxRows: Math.max(requiredRows, tableScale.maxRows),
        maxColumns: Math.max(requiredCols, tableScale.maxColumns),
      };

      if (isEqual(newTableScale, tableScale)) {
        return { success: true };
      }

      try {
        await mainSurveyClient.updateSurveyBlock(pasteUpperParentIds.surveyId, pasteUpperParentIds.id, {
          contentObject: newTableScale,
        });

        return { success: true, newTableScale };
      } catch (e) {
        const errorText = "Failed to update table scale";
        setBlockError({ text: errorText });
        return { success: false, errorText };
      }
    },
    [pasteUpperParentIds, copiedPositionInstructions, tableScale]
  );

  const handleCreateBlocks = useCallback(
    async (
      changedBlocksToPaste: BlockToPaste[]
    ): Promise<{ success: boolean; changedBlocksToPaste: BlockToPaste[] }> => {
      setLoaderText(`Copying block (1/${changedBlocksToPaste.length})`);
      let workingOnBlock = changedBlocksToPaste[0].id;
      try {
        for (const block of changedBlocksToPaste) {
          workingOnBlock = block.id;
          if (block.isCreated) continue;

          if (!block.isPasteAsExisting && block.newFieldId && !validate.isValidFieldId(block.newFieldId)) {
            setBlockError({ blockId: block.id, text: validate.getFieldIdError(block.newFieldId) || "Invalid field ID" });
            return { success: false, changedBlocksToPaste };
          }

          const newBlock = {
            contentType: block.blockData.contentType,
            isRequired: block.blockData.isRequired,
            previewDisplayType: block.blockData.previewDisplayType,
            order: pasteToIndex! + changedBlocksToPaste.indexOf(block),
            surveyId: pasteParentId!,
          };

          if (block.isPasteAsExisting) {
            Object.assign(newBlock, { objectId: block.oldObjectId });
          } else {
            // For table/chart blocks with children, omit positionInstructions from the shell —
            // they will be rebuilt from newly-created child blocks below.
            const contentObject = block.childBlocks
              ? { ...block.blockData.contentObject, fieldId: block.newFieldId, positionInstructions: undefined }
              : { ...block.blockData.contentObject, fieldId: block.newFieldId };
            Object.assign(newBlock, { contentObject });
          }

          const data = await mainSurveyClient.createSurveyBlock(pasteParentId!, newBlock);
          Object.assign(block, { newObjectId: data.data.objectId, newId: data.data.id, isCreated: true });

          if (block.childBlocks?.length) {
            for (const child of block.childBlocks) {
              if (child.isCreated) continue;
              const childPayload: Record<string, unknown> = {
                contentType: child.blockData.contentType,
                isRequired: child.blockData.isRequired,
                previewDisplayType: child.blockData.previewDisplayType,
                order: child.blockData.order,
                surveyId: block.newObjectId!,
              };
              if (child.isPasteAsExisting) {
                childPayload.objectId = child.oldObjectId;
              } else {
                childPayload.contentObject = { ...child.blockData.contentObject, fieldId: child.newFieldId };
              }
              const childData = await mainSurveyClient.createSurveyBlock(block.newObjectId!, childPayload);
              Object.assign(child, {
                newObjectId: childData.data.objectId,
                newId: childData.data.id,
                isCreated: true,
              });
            }

            // Rebuild positionInstructions using the new child IDs.
            const originalPositions = block.blockData.contentObject.positionInstructions ?? {};
            const newPositionInstructions: PositionInstructions = {};
            for (const child of block.childBlocks) {
              const pos = originalPositions[String(child.oldId)];
              if (pos && child.newId) {
                newPositionInstructions[String(child.newId)] = pos;
              }
            }
            await mainSurveyClient.updateSurveyBlock(pasteParentId!, block.newId!, {
              contentObject: { positionInstructions: newPositionInstructions },
            });
          }

          setBlocksToPaste([...changedBlocksToPaste]);

          setLoaderText(`Copying block (${changedBlocksToPaste.indexOf(block) + 1}/${changedBlocksToPaste.length})`);
        }

        return { success: true, changedBlocksToPaste };
      } catch (e) {
        setBlockError({ blockId: workingOnBlock, text: "Failed to create block" });
        return { success: false, changedBlocksToPaste };
      }
    },
    [pasteParentId, pasteToIndex]
  );

  const handleCreatePositionInstructions = useCallback(
    async (
      changedBlocksToPaste: BlockToPaste[],
      rowOffset: number,
      colOffset: number
    ): Promise<StageResult & { newPositionInstructions?: PositionInstructions }> => {
      setLoaderText(`Copying position instructions`);
      if (!pasteUpperParentIds?.surveyId || !pasteUpperParentIds?.id) {
        const errorText =
          "could not find the parent of the table, no position instructions pasted: please refresh and do it manually 😞";
        setBlockError({ text: errorText });
        return { success: false, errorText };
      }

      if (!copiedPositionInstructions) {
        const errorText =
          "could not find the position instructions, no position instructions pasted: please refresh and do it manually 😞";
        setBlockError({ text: errorText });
        return { success: false, errorText };
      }

      // Pre-validate: ensure no new block lands on a cell already occupied in the destination table.
      // Uses simple top-left equality (sufficient for 1x1 blocks; multi-cell overlap falls through to API validation).
      const existingCells = new Set(
        Object.values(positionInstructionsToPaste ?? {})
          .filter((p): p is StartEndRowCol => p !== undefined)
          .map((p) => `${p.rowStart}:${p.colStart}`)
      );
      for (const block of changedBlocksToPaste) {
        const orig = copiedPositionInstructions[String(block.id)];
        if (!orig) continue;
        const newRowStart = orig.rowStart + rowOffset;
        const newColStart = orig.colStart + colOffset;
        if (newRowStart < 1 || newColStart < 1) {
          const errorText = "Paste position is out of bounds. Increase the row or column offset.";
          setBlockError({ text: errorText });
          return { success: false, errorText };
        }
        if (existingCells.has(`${newRowStart}:${newColStart}`)) {
          const errorText =
            "Paste position overlaps with existing blocks. Adjust the row and column offset in the paste position section.";
          setBlockError({ text: errorText });
          return { success: false, errorText };
        }
      }

      const newPositionInstructionsToPaste: PositionInstructions = { ...positionInstructionsToPaste };
      changedBlocksToPaste.forEach((block) => {
        const orig = copiedPositionInstructions[String(block.id)];
        if (!orig) return;
        newPositionInstructionsToPaste[String(block.newId)] = {
          rowStart: orig.rowStart + rowOffset,
          rowEnd: orig.rowEnd + rowOffset,
          colStart: orig.colStart + colOffset,
          colEnd: orig.colEnd + colOffset,
        };
      });

      await mainSurveyClient.updateSurveyBlock(pasteUpperParentIds.surveyId, pasteUpperParentIds.id, {
        contentObject: {
          positionInstructions: newPositionInstructionsToPaste,
        },
      });

      return { success: true, newPositionInstructions: newPositionInstructionsToPaste };
    },
    [pasteUpperParentIds, positionInstructionsToPaste, copiedPositionInstructions]
  );

  const handleCreateFormattingInstructions = useCallback(async (): Promise<StageResult> => {
    setLoaderText(`Copying formatting instructions`);
    if (!pasteUpperParentIds?.surveyId || !pasteUpperParentIds?.id) {
      const errorText =
        "could not find the parent of the table, no formatting instructions pasted: please refresh and do it manually 😞";
      setBlockError({ text: errorText });
      return { success: false, errorText };
    }

    try {
      const positions = Object.values(copiedPositionInstructions ?? {}).map((position) =>
        startEndRowColToCellPosition(position)
      );
      const rulesToPaste = getFormattingRules(copiedFormattingInstructions, {
        ruleType: FormattingRuleType.Cell,
        position: (p) => positions.includes(String(p)),
      });

      if (!rulesToPaste?.length) return { success: true };

      await mainSurveyClient.updateSurveyBlock(pasteUpperParentIds.surveyId, pasteUpperParentIds.id, {
        contentObject: {
          formattingInstructions: [...formattingInstructionsToPaste, ...rulesToPaste],
        },
      });

      return { success: true };
    } catch (e) {
      const errorText = "Failed to create formatting instructions: please refresh and do it manually 😞";
      setBlockError({ text: errorText });
      return { success: false, errorText };
    }
  }, [copiedFormattingInstructions, copiedPositionInstructions, formattingInstructionsToPaste, pasteUpperParentIds]);

  const handleCreateConditions = useCallback(
    async (changedBlocksToPaste: BlockToPaste[]): Promise<StageResult> => {
      setLoaderText(`Copying conditions (1/${changedBlocksToPaste.length})`);
      try {
        for (const block of changedBlocksToPaste) {
          const newConditions: Partial<Conditions.Condition>[] = block.blockData.conditions.map((condition) => {
            const newObjectId = changedBlocksToPaste.find((b) => b.oldObjectId === condition.objectId)?.newObjectId;
            return {
              objectId: newObjectId ?? condition.objectId,
              value: condition.value,
              operatorType: condition.operatorType,
              actionType: condition.actionType,
              contentType: condition.contentType,
            };
          });

          for (const condition of newConditions) {
            await conditionClient.createCondition(pasteParentId!, block.newId!, condition);
          }
          setLoaderText(
            `Copying conditions (${changedBlocksToPaste.indexOf(block) + 1}/${changedBlocksToPaste.length})`
          );
        }

        return { success: true };
      } catch (e) {
        const errorText = "Failed to create conditions: please refresh and do it manually 😞";
        setBlockError({ text: errorText });
        return { success: false, errorText };
      }
    },
    [pasteParentId]
  );

  const handleCreateSupportingInfo = useCallback(
    async (changedBlocksToPaste: BlockToPaste[]): Promise<StageResult> => {
      setLoaderText(`Copying supporting information (1/${changedBlocksToPaste.length})`);
      try {
        for (const block of changedBlocksToPaste) {
          for (const info of block.blockData.relatedSupportingInformation) {
            await relatedSupportingInfoClient.addSupportingInfoToBlock(
              pasteParentId!,
              block.newId!,
              info.supportingInformation.id,
              info.tabName
            );
          }
          setLoaderText(
            `Copying supporting information (${changedBlocksToPaste.indexOf(block) + 1}/${changedBlocksToPaste.length})`
          );
        }

        return { success: true };
      } catch (e) {
        const errorText = "Failed to create supporting information: please refresh and do it manually 😞";
        setBlockError({ text: errorText });
        return { success: false, errorText };
      }
    },
    [pasteParentId]
  );

  const createBlocks = useCallback(async () => {
    setIsLoading(true);
    setBlockError(null);

    // Compute the row/col offset: translate the top-left of the copied selection to the chosen anchor.
    let rowOffset = 0;
    let colOffset = 0;
    if (copiedPositionInstructions) {
      const positions = Object.values(copiedPositionInstructions).filter((p): p is StartEndRowCol => p !== undefined);
      if (positions.length) {
        const minRow = Math.min(...positions.map((p) => p.rowStart));
        const minCol = Math.min(...positions.map((p) => p.colStart));
        rowOffset = pasteAnchorRow - minRow;
        colOffset = pasteAnchorCol - minCol;
      }
    }

    let newTableScale: { maxRows: number; maxColumns: number } | undefined;
    if (tableScale && pasteUpperParentIds) {
      const result = await handleChangeTableScale(rowOffset, colOffset);
      if (!result.success) {
        setLoaderText("");
        setIsLoading(false);
        return;
      }
      newTableScale = result.newTableScale;
    }

    const { success, changedBlocksToPaste } = await handleCreateBlocks(blocksToPaste);
    if (!success) {
      setLoaderText("");
      setIsLoading(false);
      return;
    }

    let newPositionInstructions: PositionInstructions | undefined;
    if (copiedPositionInstructions) {
      const result = await handleCreatePositionInstructions(changedBlocksToPaste, rowOffset, colOffset);
      if (!result.success) {
        setLoaderText("");
        setIsLoading(false);
        return;
      }
      newPositionInstructions = result.newPositionInstructions;
    }

    if (copiedFormattingInstructions.length) {
      const result = await handleCreateFormattingInstructions();
      if (!result.success) {
        setLoaderText("");
        setIsLoading(false);
        return;
      }
    }

    if (isPasteConditions) {
      const result = await handleCreateConditions(changedBlocksToPaste);
      if (!result.success) {
        setLoaderText("");
        setIsLoading(false);
        return;
      }
    }

    if (isPasteRelatedSupportingInfo) {
      const result = await handleCreateSupportingInfo(changedBlocksToPaste);
      if (!result.success) {
        setLoaderText("");
        setIsLoading(false);
        return;
      }
    }

    setLoaderText("");
    setIsLoading(false);
    setIsPasteDialogOpen(false);
    onSuccessRef.current?.({ newPositionInstructions, newTableScale, createdBlocks: changedBlocksToPaste });
    onSuccessRef.current = null;
  }, [
    copiedPositionInstructions,
    pasteAnchorRow,
    pasteAnchorCol,
    tableScale,
    pasteUpperParentIds,
    handleCreateBlocks,
    blocksToPaste,
    copiedFormattingInstructions,
    isPasteConditions,
    isPasteRelatedSupportingInfo,
    handleChangeTableScale,
    handleCreatePositionInstructions,
    handleCreateFormattingInstructions,
    handleCreateConditions,
    handleCreateSupportingInfo,
  ]);

  const handleSetCopiedBlocks = useCallback((blocks: Blockable[]) => {
    setCopiedBlocks(blocks);
  }, []);

  return {
    isPasteDialogOpen,
    isPasteConditions,
    setIsPasteConditions,
    isPasteRelatedSupportingInfo,
    setIsPasteRelatedSupportingInfo,
    setCopiedFormattingInstructions,
    setFormattingInstructionsToPaste,
    setPositionInstructionsToPaste,
    copiedBlocks,
    setCopiedBlocks: handleSetCopiedBlocks,
    blocksToPaste,
    canCreate,
    loaderText,
    isLoading,
    blockError,
    startPaste,
    cancelPaste,
    findAndReplaceForAllFieldIds,
    handleNewFieldIdChange,
    handleNewChildFieldIdChange,
    handleIsPasteAsExistingChange,
    handleIsPasteAsExistingChildChange,
    removeBlockToPaste,
    createBlocks,
    setCopiedPositionInstructions,
    isTablePaste,
    pasteAnchorRow,
    setPasteAnchorRow,
    pasteAnchorCol,
    setPasteAnchorCol,
    tableScale,
    positionInstructionsToPaste,
    copiedPositionInstructions,
  };
};

interface ChildBlockToPaste {
  oldId: number;
  oldObjectId: number;
  oldFieldId: string;
  newFieldId: string;
  newId?: number;
  newObjectId?: number;
  isCreated: boolean;
  isPasteAsExisting?: boolean;
  blockData: SurveyBlocks.Block;
}

interface BlockToPaste {
  parentSurveyId: string | number;
  id: string | number;
  oldFieldId: string;
  newFieldId: string;
  oldObjectId: string | number;
  newObjectId?: string | number;
  newId?: string | number;
  isCreated: boolean;
  blockData: SurveyBlocks.Block;
  isPasteAsExisting?: boolean;
  childBlocks?: ChildBlockToPaste[];
}

export type CopyAndPasteBlocksStore = ReturnType<typeof useCopyAndPasteBlocks>;
